import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { 
  Cat, 
  Syringe, 
  Calendar, 
  ClipboardList, 
  Utensils, 
  Stethoscope,
  Plus,
  Filter,
  Heart,
  Activity,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { petsService, Pet } from '../../lib/petsService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { SearchInput } from '../common/SearchInput';
import { PetProfiles } from './PetProfiles';
import { DietNutrition } from './DietNutrition';
import { MedicalHistory } from './MedicalHistory';
import { PetCareSchedule } from './PetCareSchedule';
import { VaccinationRecords } from './VaccinationRecords';
import { VeterinaryAppointments } from './VeterinaryAppointments';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  route: string;
}

const navItems: NavItem[] = [
  { 
    id: 'profiles', 
    label: 'Pet Profiles', 
    icon: <Cat size={24} />,
    description: 'Manage your pet profiles and information',
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    route: '/dashboard/pets/profiles'
  },
  { 
    id: 'diet', 
    label: 'Diet & Nutrition', 
    icon: <Utensils size={24} />,
    description: 'Track feeding schedules and dietary needs',
    color: 'bg-green-50 text-green-600 border-green-200',
    route: '/dashboard/pets/diet'
  },
  { 
    id: 'care', 
    label: 'Care Schedule', 
    icon: <ClipboardList size={24} />,
    description: 'Manage feeding and care routines',
    color: 'bg-purple-50 text-purple-600 border-purple-200',
    route: '/dashboard/pets/care'
  },
  { 
    id: 'health', 
    label: 'Health Records', 
    icon: <Stethoscope size={24} />,
    description: 'Track medical records and treatments',
    color: 'bg-red-50 text-red-600 border-red-200',
    route: '/dashboard/pets/health'
  },
  { 
    id: 'vaccinations', 
    label: 'Vaccinations', 
    icon: <Syringe size={24} />,
    description: 'Manage vaccination records and schedules',
    color: 'bg-blue-50 text-blue-600 border-blue-200',
    route: '/dashboard/pets/vaccinations'
  },
  { 
    id: 'appointments', 
    label: 'Vet Appointments', 
    icon: <Calendar size={24} />,
    description: 'Schedule and track veterinary visits',
    color: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    route: '/dashboard/pets/appointments'
  }
];

function PetsDashboard({ pets, error, onRetry }: { pets: Pet[]; error: string | null; onRetry: () => void }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pet Management</h1>
          <p className="text-gray-600 mt-1">Comprehensive care for your beloved pets</p>
        </div>
        <Button
          onClick={() => navigate('/dashboard/pets/profiles')}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={20} className="mr-2" />
          Add Pet
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Heart size={24} className="text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Pets</p>
              <p className="text-2xl font-bold text-gray-900">{pets.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <Calendar size={24} className="text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Appointments</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-50 rounded-lg">
              <Syringe size={24} className="text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vaccinations</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg">
              <Activity size={24} className="text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Care Tasks</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search pets or records..."
          className="flex-1"
        />
        <Button variant="outline" className="flex items-center gap-2">
          <Filter size={20} />
          Filters
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {navItems.map((item) => (
          <Card
            key={item.id}
            className="cursor-pointer h-full hover:shadow-lg transition-shadow duration-200 group"
            onClick={() => navigate(item.route)}
          >
            <div className="p-6 space-y-4">
              <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center border group-hover:scale-110 transition-transform duration-200`}>
                {item.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition-colors duration-200">{item.label}</h3>
                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="w-full justify-start h-12 hover:bg-blue-50 hover:border-blue-200"
              onClick={() => navigate('/dashboard/pets/profiles')}
            >
              <Cat size={20} className="mr-2" />
              Add New Pet
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12 hover:bg-green-50 hover:border-green-200"
              onClick={() => navigate('/dashboard/pets/care')}
            >
              <ClipboardList size={20} className="mr-2" />
              Add Care Schedule
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12 hover:bg-red-50 hover:border-red-200"
              onClick={() => navigate('/dashboard/pets/health')}
            >
              <Stethoscope size={20} className="mr-2" />
              Add Health Record
            </Button>
          </div>
        </div>
      </Card>

      {pets.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Pets</h2>
            <div className="space-y-3">
              {pets.map((pet) => (
                <div key={pet.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Cat size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{pet.name}</p>
                      <p className="text-sm text-gray-500">{pet.species} {pet.breed ? `• ${pet.breed}` : ''}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/dashboard/pets/profiles')}
                    className="hover:bg-blue-50 hover:text-blue-600"
                  >
                    View Details
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function SubRouteWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/dashboard/pets')} className="mb-4">
        <ArrowLeft size={20} className="mr-2" />
        Back to Pet Management
      </Button>
      {children}
    </div>
  );
}

export function PetsManagement() {
  const { user } = useAuth();
  const [pets, setPets] = useState<Pet[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetchPets = useCallback(async () => {
    if (!user) return;
    try {
      setError(null);
      const data = await petsService.getPets();
      setPets(data);
    } catch (err) {
      console.error('Failed to load pets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pets');
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  useEffect(() => {
    const handler = () => fetchPets();
    window.addEventListener('refresh-pets', handler);
    return () => window.removeEventListener('refresh-pets', handler);
  }, [fetchPets]);

  return (
    <Routes>
      <Route path="/" element={<PetsDashboard pets={pets} error={error} onRetry={fetchPets} />} />
      <Route path="/profiles" element={<SubRouteWrapper><PetProfiles /></SubRouteWrapper>} />
      <Route path="/diet" element={<SubRouteWrapper><DietNutrition /></SubRouteWrapper>} />
      <Route path="/care" element={<SubRouteWrapper><PetCareSchedule /></SubRouteWrapper>} />
      <Route path="/health" element={<SubRouteWrapper><MedicalHistory /></SubRouteWrapper>} />
      <Route path="/vaccinations" element={<SubRouteWrapper><VaccinationRecords /></SubRouteWrapper>} />
      <Route path="/appointments" element={<SubRouteWrapper><VeterinaryAppointments /></SubRouteWrapper>} />
      <Route path="*" element={<Navigate to="/dashboard/pets" replace />} />
    </Routes>
  );
}
